/**
 * The Settings draft: refurbishment default and shared assumptions are edited
 * as one draft and persisted by one explicit Save. Section and group switches
 * keep the draft. Refurbishment saves first (preferences, revision-fenced);
 * the assumptions follow (deal-model settings, revision-fenced). A partial
 * failure keeps the unsaved edits and says which part was saved.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../../api/http'
import { usePatchPreferences, usePreferences } from '../../api/queries'
import { useDealModelUserSettings, usePutDealModelUserSettings } from '../../api/settings'
import { assertJsonUtf8Ceiling, DEAL_MODEL_RESOURCE_LIMITS, validateUserSettingsRequestValues } from '../../api/dealModelContract'
import { refurbishmentDraft, resolveRefurbishmentDraft, type RefurbishmentDraft } from './refurbishment'
import { settingsFieldPresentation, type SettingsValue } from './settingsFieldPresentation'

export function useSettingsDraft(canReadDefaults: boolean) {
  const settings = useDealModelUserSettings(canReadDefaults)
  const saveSettings = usePutDealModelUserSettings()
  const preferences = usePreferences()
  const savePreferences = usePatchPreferences()
  const [refurbishment, setRefurbishment] = useState<RefurbishmentDraft>(() => refurbishmentDraft())
  const [refurbishmentDirty, setRefurbishmentDirty] = useState(false)
  const [assumptions, setAssumptions] = useState<Record<string, SettingsValue>>({})
  const [dirty, setDirty] = useState(false)
  const [editedKeys, setEditedKeys] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const saveLock = useRef(false)
  const editGeneration = useRef(0)

  const busy = saving || saveSettings.isPending || savePreferences.isPending
  const hasChanges = dirty || refurbishmentDirty

  // Personal overrides are sparse: untouched keys keep their saved state (absent = source default).
  const submittedAssumptions = useMemo(() => {
    const values: Record<string, SettingsValue> = { ...settings.data?.defaultAssumptions }
    for (const key of editedKeys) values[key] = assumptions[key] ?? null
    return values
  }, [settings.data, editedKeys, assumptions])

  const invalidKey = useMemo(() => {
    if (!settings.data || !dirty) return null
    const descriptors = settings.data.defaultAssumptionDescriptors
    return descriptors.find(descriptor => {
      if (!(descriptor.key in submittedAssumptions)) return false
      try { validateUserSettingsRequestValues({ [descriptor.key]: submittedAssumptions[descriptor.key] ?? null }, [descriptor], 'defaultAssumptions'); return false }
      catch { return true }
    })?.key ?? null
  }, [settings.data, dirty, submittedAssumptions])

  const exactValueError = useMemo(() => {
    if (!settings.data) return null
    try {
      const exact = validateUserSettingsRequestValues(submittedAssumptions, settings.data.defaultAssumptionDescriptors, 'defaultAssumptions')
      assertJsonUtf8Ceiling({ expectedRevision: settings.data.settingsRevision, defaultAssumptions: exact }, 'user settings request body', DEAL_MODEL_RESOURCE_LIMITS.evaluationInputUtf8Bytes)
      return null
    } catch {
      const invalid = settings.data.defaultAssumptionDescriptors.find(descriptor => descriptor.key === invalidKey)
      return invalid ? `Check ${settingsFieldPresentation(invalid).label.toLowerCase()}. Enter a valid value before saving.` : 'Check your values before saving.'
    }
  }, [submittedAssumptions, settings.data, invalidKey])

  const { preference: refurbishmentPreference, error: refurbishmentError } = useMemo(() => {
    try { return { preference: resolveRefurbishmentDraft(refurbishment), error: null } }
    catch (error) { return { preference: null, error: error instanceof Error ? error.message : 'Choose a valid refurbishment budget before saving.' } }
  }, [refurbishment])

  useEffect(() => {
    if (preferences.data && !refurbishmentDirty && !saving) setRefurbishment(refurbishmentDraft(preferences.data.refurbishmentDefault))
  }, [preferences.data, refurbishmentDirty, saving])
  useEffect(() => {
    if (!settings.data || dirty || saving) return
    // Show the saved source defaults with personal overrides taking precedence.
    setAssumptions({ ...settings.data.sourceDefaultAssumptions, ...settings.data.defaultAssumptions })
    setEditedKeys([])
  }, [dirty, settings.data, saving])

  const updateAssumption = (key: string, value: SettingsValue) => {
    if (saveLock.current) return
    editGeneration.current += 1
    setAssumptions(current => ({ ...current, [key]: value }))
    setEditedKeys(current => current.includes(key) ? current : [...current, key])
    setDirty(true)
    setSaveMessage(null)
  }
  const updateRefurbishment = (next: RefurbishmentDraft) => {
    if (saveLock.current) return
    editGeneration.current += 1
    setRefurbishment(next)
    setRefurbishmentDirty(true)
    setSaveMessage(null)
  }

  const canSave = canReadDefaults && hasChanges && Boolean(settings.data) && Boolean(preferences.data) && !(dirty && exactValueError) && Boolean(refurbishmentPreference)

  const save = async () => {
    if (saveLock.current || !canReadDefaults || !settings.data || !preferences.data || !hasChanges || (dirty && exactValueError) || !refurbishmentPreference) return
    saveLock.current = true
    setSaving(true)
    setSaveMessage(null)
    const submittedGeneration = editGeneration.current
    let savedRefurbishment = false
    try {
      if (refurbishmentDirty) {
        await savePreferences.mutateAsync({ refurbishmentDefault: refurbishmentPreference, expectedRefurbishmentDefaultRevision: preferences.data.refurbishmentDefaultRevision })
        savedRefurbishment = true
        setRefurbishmentDirty(false)
      }
      if (dirty) await saveSettings.mutateAsync({ expectedRevision: settings.data.settingsRevision, defaultAssumptions: submittedAssumptions, descriptors: settings.data.defaultAssumptionDescriptors })
      if (editGeneration.current === submittedGeneration) { setDirty(false); setEditedKeys([]) }
      // Recalculation status belongs to the application header; a save is not completion.
      setSaveMessage('Defaults saved.')
    } catch {
      if (savedRefurbishment) setSaveMessage('Refurbishment saved. Your other defaults were not saved; their changes are still here.')
    } finally { saveLock.current = false; setSaving(false) }
  }

  const conflict = (error: unknown) => error instanceof ApiError && error.status === 409

  return {
    settings, preferences, saveSettings, savePreferences,
    refurbishment, refurbishmentDirty, refurbishmentError, updateRefurbishment,
    assumptions, dirty, exactValueError, invalidKey, updateAssumption,
    busy, hasChanges, canSave, save, saveMessage, conflict,
  }
}

export type SettingsDraft = ReturnType<typeof useSettingsDraft>
